/**
 * Mindfulness Service
 * Handles data fetching and catalog resolution for mindfulness practices
 */
import api from "../axiosconfig";

/**
 * Fetches the mindfulness exercise catalog and user favourites in parallel.
 * Falls back to local static JSON if the backend API is unreachable.
 *
 * @returns {Promise<{ exercises: Array, mindfulnessCategory: Object, userFavourites: Array }>}
 */
export const fetchMindfulnessCatalog = async () => {
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [exercisesRes, favsRes] = await Promise.allSettled([
    api.get(`${API_URL}/api/exercises`),
    api.post(`${API_URL}/exercises`, { request: "getFavourites" }),
  ]);

  let exercises = [];
  if (
    exercisesRes.status === "fulfilled" &&
    exercisesRes.value?.data?.exercises &&
    Array.isArray(exercisesRes.value.data.exercises) &&
    exercisesRes.value.data.exercises.length > 0
  ) {
    exercises = exercisesRes.value.data.exercises;
  }

  // Fallback: If backend exercises endpoint fails or returns empty, load from static files
  if (exercises.length === 0) {
    try {
      const txtRes = await fetch(`/ExerciseFiles/fullList.txt?t=${Date.now()}`);
      const txtData = await txtRes.text();
      const fileNames = txtData
        .split("\n")
        .map((f) => f.trim())
        .filter((f) => f.length > 0 && f.endsWith(".json"));

      const filePromises = fileNames.map(async (fileName) => {
        try {
          const res = await fetch(`/ExerciseFiles/${fileName}?t=${Date.now()}`);
          const jsonData = await res.json();
          return Array.isArray(jsonData) ? jsonData : [jsonData];
        } catch {
          return [];
        }
      });
      const results = await Promise.all(filePromises);
      exercises = results.flat();
    } catch (fallbackErr) {
      console.warn("Fallback to static exercise files failed:", fallbackErr);
    }
  }

  // Extract user favourites
  const favsSet = new Set();
  if (
    favsRes.status === "fulfilled" &&
    Array.isArray(favsRes.value?.data?.result)
  ) {
    favsRes.value.data.result.forEach((item) => {
      if (item && typeof item === "string") {
        favsSet.add(item.trim().toLowerCase());
      }
    });
  }

  // Build category mapping and merge is_favourited flags from exercises
  const categoriesMap = {};
  exercises.forEach((ex) => {
    const key = (ex.title || "").trim().toLowerCase();
    categoriesMap[key] = ex.tags || [];
    if (ex.is_favourited) {
      favsSet.add(key);
    }
  });

  return {
    exercises,
    mindfulnessCategory: categoriesMap,
    userFavourites: Array.from(favsSet),
  };
};

/**
 * Toggles an exercise favourite status on the backend.
 *
 * @param {string} title
 * @param {boolean} isFavourited
 * @returns {Promise<any>}
 */
export const toggleExerciseFavouriteApi = async (title, isFavourited) => {
  const API_URL = process.env.REACT_APP_API_URL || "";
  const requestType = isFavourited ? "favourite" : "unfavourite";
  const res = await api.post(`${API_URL}/exercises`, {
    request: requestType,
    exercise: title,
  });
  if (res?.data?.result === -1 || res?.data?.success === false) {
    throw new Error(res?.data?.message || "Failed to update favourite status");
  }
  return res;
};

/**
 * Marks an exercise complete on the backend.
 *
 * @param {string|number} exerciseIdOrSlug
 * @param {number} duration
 * @returns {Promise<any>}
 */
export const completeExerciseApi = async (exerciseIdOrSlug, duration = 0) => {
  const API_URL = process.env.REACT_APP_API_URL || "";
  const res = await api.post(`${API_URL}/exercises`, {
    request: "complete",
    exercise: exerciseIdOrSlug,
    duration,
  });
  if (res?.data?.result === -1 || res?.data?.success === false) {
    throw new Error(res?.data?.message || "Failed to complete exercise");
  }
  return res;
};


